/**
 * A CHAIN of walls: the walls a user draws tap by tap, joined end to end, and
 * the two face polylines a room is cut along.
 *
 * A single wall is a chain of one. A room's outline runs on wall FACES (the
 * pipeline's polygons exclude wall bodies), so a chain cuts its room along the
 * lines half a thickness either side of its centreline — mitred at every bend
 * so the two faces of a bent wall meet in a point rather than overlapping or
 * leaving a notch. Every vertex a chain split adds to a room is one of those
 * mitre points or one of the four places the two faces meet the boundary
 * (`chainCut.js`).
 *
 * A chain's identity is never stored. It is worked out each time from the
 * walls joined end to end (`chainContaining`), so deleting or extending one
 * wall needs no bookkeeping.
 *
 * Pure. X-Z plane; Y is up; increasing Z is screen-down (CLAUDE.md).
 */

const { wallFrame, distanceToPolygonBoundary } = require('./geometry');

/** Two wall ends this close are the same joint. */
const JOIN_TOL_M = 0.01;
/** A chain end this close to the room boundary touches it. The drawing tool
 *  snaps a tapped end exactly onto a face, so this only absorbs rounding. */
const TOUCH_TOL_M = 0.01;

const PARALLEL_EPS = 1e-9;

const dist = (a, b) => Math.hypot(a.x - b.x, a.z - b.z);
const mid = (a, b) => ({ x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 });

/** Same storey, where both say. An unstamped storey matches anything. */
function sameStorey(a, b) {
  return (
    !Number.isFinite(a?.story) ||
    !Number.isFinite(b?.story) ||
    a.story === b.story
  );
}

/** The two centreline ends of a usable wall, or null. */
function centrelineEnds(wall) {
  if (!wall?.center || !(wall?.dimensions?.x > 0)) return null;
  const f = wallFrame(wall);
  return { ends: f.ends, half: f.half };
}

/** Indexes of the other ends (of OTHER walls in `list`) within JOIN_TOL_M of `p`. */
function endsAt(p, list, skipIndex) {
  const out = [];
  list.forEach((item, i) => {
    if (i === skipIndex) return;
    item.ends.forEach((q, k) => {
      if (dist(p, q) <= JOIN_TOL_M) out.push({ i, k });
    });
  });
  return out;
}

/**
 * Order `structureWallIds` into a chain joined end to end.
 *
 * The ids may come in any order and each wall in either direction; the chain
 * comes back in travel order with every stretch oriented along it. When the
 * first id is an end of the chain, travel starts from it. Two joined ends meet
 * at their midpoint, so each stretch's `b` is exactly the next one's `a`.
 *
 * @returns {{ ok: true, wallIds: string[], points: {x,z}[],
 *             stretches: Array<{ wallId, a: {x,z}, b: {x,z}, half: number }> }
 *         | { ok: false, reason: 'wall-not-found'|'degenerate-wall'|'not-a-chain' }}
 */
function buildChain(structureWalls, structureWallIds) {
  const ids = Array.isArray(structureWallIds) ? structureWallIds : [];
  if (ids.length === 0) return { ok: false, reason: 'wall-not-found' };
  const byId = new Map(
    (structureWalls || []).filter((w) => w?.id).map((w) => [w.id, w])
  );
  const items = [];
  for (const id of ids) {
    const wall = byId.get(id);
    if (!wall) return { ok: false, reason: 'wall-not-found' };
    const ce = centrelineEnds(wall);
    if (!ce) return { ok: false, reason: 'degenerate-wall' };
    items.push({ id, ...ce });
  }
  if (new Set(ids).size !== ids.length) {
    return { ok: false, reason: 'not-a-chain' };
  }

  // joins[i][k] = the one { i, k } wall end joined to wall i's end k, or null.
  const joins = [];
  for (let i = 0; i < items.length; i++) {
    joins.push([]);
    for (let k = 0; k < 2; k++) {
      const at = endsAt(items[i].ends[k], items, i);
      if (at.length > 1) return { ok: false, reason: 'not-a-chain' };
      joins[i].push(at[0] || null);
    }
    if (joins[i][0] && joins[i][1] && joins[i][0].i === joins[i][1].i) {
      return { ok: false, reason: 'not-a-chain' }; // two walls forming a loop
    }
  }

  // Start at an end of the chain: the first id when it is one, else the first
  // wall (in the given order) with a free end. A single wall runs ends[0] →
  // ends[1], its own direction.
  const isEnd = (i) => !joins[i][0] || !joins[i][1];
  const start = isEnd(0) ? 0 : items.findIndex((_, i) => isEnd(i));
  if (start < 0) return { ok: false, reason: 'not-a-chain' }; // a closed loop

  const order = [];
  let i = start;
  let entryEnd = joins[start][0] ? (joins[start][1] ? 0 : 1) : 0;
  const seen = new Set();
  while (i != null && !seen.has(i)) {
    seen.add(i);
    const exitEnd = 1 - entryEnd;
    order.push({ i, entryEnd, exitEnd });
    const next = joins[i][exitEnd];
    if (!next) break;
    i = next.i;
    entryEnd = next.k;
  }
  if (order.length !== items.length)
    return { ok: false, reason: 'not-a-chain' };

  const points = [];
  const stretches = [];
  for (let s = 0; s < order.length; s++) {
    const { i: wi, entryEnd: e0, exitEnd: e1 } = order[s];
    const item = items[wi];
    // A joint is the midpoint of the two ends that meet there, shared by
    // reference so the previous stretch's `b` IS this one's `a`.
    const a =
      s > 0 ? stretches[s - 1].b : { x: item.ends[e0].x, z: item.ends[e0].z };
    const nxt = order[s + 1];
    const b = nxt
      ? mid(item.ends[e1], items[nxt.i].ends[nxt.entryEnd])
      : { x: item.ends[e1].x, z: item.ends[e1].z };
    stretches.push({ wallId: item.id, a, b, half: item.half });
    if (s === 0) points.push(a);
    points.push(b);
  }

  return {
    ok: true,
    wallIds: order.map((o) => items[o.i].id),
    points,
    stretches,
  };
}

/**
 * The walls joined end to end beyond a chain's two ends, walking outward.
 *
 * From each end it continues through joints where exactly one other wall ends,
 * and stops at an end that touches `boundaryPolygon` (within `TOUCH_TOL_M`), at
 * a free end, or at a junction of three or more walls. Only walls on the
 * storey of `storeyOf` join. This is how a chain the user starts on the free
 * end of a stub is judged together with the stub (spec §4.4).
 *
 * @param {object[]} structureWalls
 * @param {{ wallIds: string[], points: {x,z}[] }} chain a `buildChain` result
 * @param {{x,z}[]} [boundaryPolygon] the room the chain stands in
 * @param {object} [storeyOf] the wall whose storey the walk keeps to
 * @returns {string[]} the chain's ids extended both ways, in chain order
 */
function extendChainIds(structureWalls, chain, boundaryPolygon, storeyOf) {
  const walls = (structureWalls || []).filter((w) => w?.id);
  const inChain = new Set(chain.wallIds);
  const candidates = walls
    .filter((w) => !inChain.has(w.id) && sameStorey(w, storeyOf))
    .map((w) => ({ wall: w, ce: centrelineEnds(w) }))
    .filter((c) => c.ce);
  const ring = Array.isArray(boundaryPolygon) ? boundaryPolygon : [];
  const touches = (p) =>
    ring.length >= 3 && distanceToPolygonBoundary(p, ring) <= TOUCH_TOL_M;

  /** Walk outward from `p`, the free end of the last wall added. */
  const walk = (p) => {
    const out = [];
    let at = p;
    for (;;) {
      if (touches(at)) break;
      const meeting = [];
      for (const c of candidates) {
        c.ce.ends.forEach((q, k) => {
          if (dist(at, q) <= JOIN_TOL_M) meeting.push({ c, k });
        });
      }
      if (meeting.length !== 1) break; // a free end, or a junction of 3+
      const { c, k } = meeting[0];
      if (inChain.has(c.wall.id)) break; // a loop
      inChain.add(c.wall.id);
      out.push(c.wall.id);
      at = c.ce.ends[1 - k];
    }
    return out;
  };

  const back = walk(chain.points[0]);
  const fwd = walk(chain.points[chain.points.length - 1]);
  return [...back.reverse(), ...chain.wallIds, ...fwd];
}

/**
 * Every wall joined end to end with `wallId`, in chain order — the chain the
 * wall belongs to (`extendChainIds` from a chain of one).
 *
 * @param {object[]} structureWalls
 * @param {string} wallId
 * @param {{x,z}[]} [boundaryPolygon] the room the chain stands in
 * @returns {string[]} ids in chain order; `[]` when `wallId` is unusable
 */
function chainContaining(structureWalls, wallId, boundaryPolygon) {
  const wall = (structureWalls || []).find((w) => w?.id === wallId);
  const one = wall ? buildChain([wall], [wallId]) : null;
  if (!one?.ok) return [];
  return extendChainIds(structureWalls, one, boundaryPolygon, wall);
}

/** Intersection of two lines p + t·d, or null when parallel. */
function intersectLines(p1, d1, p2, d2) {
  const den = d1.x * d2.z - d1.z * d2.x;
  if (Math.abs(den) < PARALLEL_EPS) return null;
  const t = ((p2.x - p1.x) * d2.z - (p2.z - p1.z) * d2.x) / den;
  return { x: p1.x + d1.x * t, z: p1.z + d1.z * t };
}

/** A stretch's unit direction and left normal (the `wallFrame` convention). */
function stretchFrame(st) {
  const len = dist(st.a, st.b) || 1;
  const dir = { x: (st.b.x - st.a.x) / len, z: (st.b.z - st.a.z) / len };
  return { dir, n: { x: -dir.z, z: dir.x } };
}

/** One face polyline: each stretch offset by `sign × half`, mitred. */
function facePolyline(chain, sign) {
  const st = chain?.stretches || [];
  if (st.length === 0) return [];
  const lines = st.map((s) => {
    const { dir, n } = stretchFrame(s);
    const off = sign * s.half;
    return {
      p: { x: s.a.x + n.x * off, z: s.a.z + n.z * off },
      q: { x: s.b.x + n.x * off, z: s.b.z + n.z * off },
      dir,
      n,
      off,
    };
  });
  const out = [lines[0].p];
  for (let i = 1; i < lines.length; i++) {
    const L0 = lines[i - 1];
    const L1 = lines[i];
    const hit = intersectLines(L0.p, L0.dir, L1.p, L1.dir);
    if (hit) {
      out.push(hit);
    } else {
      // Collinear stretches: the joint, offset by the mean of their halves.
      const j = st[i].a;
      const off = (L0.off + L1.off) / 2;
      out.push({ x: j.x + L1.n.x * off, z: j.z + L1.n.z * off });
    }
  }
  out.push(lines[lines.length - 1].q);
  return out;
}

/**
 * The chain's two face polylines: `left` offset by `+half` along each
 * stretch's left normal `(-dir.z, dir.x)`, `right` by `-half`. Adjacent offset
 * lines meet at their intersection (the mitre); collinear stretches share their
 * offset point. Untrimmed — `chainCut.js` extends or clips each end to the room.
 */
function facePolylines(chain) {
  return { left: facePolyline(chain, 1), right: facePolyline(chain, -1) };
}

module.exports = {
  JOIN_TOL_M,
  TOUCH_TOL_M,
  sameStorey,
  buildChain,
  extendChainIds,
  chainContaining,
  facePolylines,
  stretchFrame,
};
